import { z } from "zod";
const id = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/iu);
const digest = z.string().regex(/^[a-f0-9]{64}$/u);
export const closeBedtimeNativeFailureCommandSchema = z
    .object({
    action: z.literal("CLOSE_FAILED_NATIVE_ARTWORK"),
    jobId: id,
    expectedStoryRevision: z.number().int().nonnegative(),
    expectedPlanDigestSha256: digest,
    reviewDigestSha256: digest,
})
    .strict();
/**
 * Owner decision 1 (2026-10-15): a stopped picture request whose provider outcome stayed unknown
 * is closed by its buyer once it has been stopped this long, each unknown call counted at the most
 * it could have cost. A provider that refused a call reports it at $0 and needs no wait.
 */
export const BEDTIME_STOPPED_REQUEST_CLOSE_AFTER_MS = 5 * 60_000;
export const bedtimeNativeFailureRecoveryOfferSchema = z
    .object({
    bookId: id,
    jobId: id,
    recordedCostMicros: z.number().int().nonnegative(),
    releasedReservationMicros: z.number().int().positive().optional(),
    /** Calls whose outcome stayed unknown, counted at their maximum in `recordedCostMicros`. */
    countedUnknownCalls: z.number().int().positive().max(20).optional(),
    historyPreserved: z.literal(true),
    providerDispatchAuthorized: z.literal(false),
    command: closeBedtimeNativeFailureCommandSchema,
})
    .strict()
    .superRefine((offer, ctx) => {
    if (offer.releasedReservationMicros !== undefined && offer.recordedCostMicros !== 0)
        ctx.addIssue({
            code: "custom",
            message: "An unused reservation has zero provider spending.",
        });
    if (offer.releasedReservationMicros !== undefined && offer.countedUnknownCalls !== undefined)
        ctx.addIssue({
            code: "custom",
            message: "A request with unknown calls never releases its reservation unused.",
        });
    if (offer.countedUnknownCalls !== undefined && offer.recordedCostMicros === 0)
        ctx.addIssue({
            code: "custom",
            message: "Unknown calls are counted at their maximum, never at zero.",
        });
    if (offer.jobId !== offer.command.jobId)
        ctx.addIssue({ code: "custom", path: ["command", "jobId"], message: "Job must match." });
});
export const bedtimeNativeFailureRecoveryReceiptSchema = z
    .object({
    bookId: id,
    jobId: id,
    recordedCostMicros: z.number().int().nonnegative(),
    releasedReservationMicros: z.number().int().positive().optional(),
    countedUnknownCalls: z.number().int().positive().max(20).optional(),
    reviewDigestSha256: digest,
    closedBy: id,
    closedAt: z.string().datetime(),
    historyPreserved: z.literal(true),
    budgetChanged: z.boolean(),
    providerDispatchAuthorized: z.literal(false),
})
    .strict()
    .superRefine((receipt, ctx) => {
    if (receipt.budgetChanged !==
        (receipt.releasedReservationMicros !== undefined ||
            receipt.countedUnknownCalls !== undefined) ||
        (receipt.releasedReservationMicros !== undefined &&
            (receipt.recordedCostMicros !== 0 || receipt.countedUnknownCalls !== undefined)) ||
        (receipt.countedUnknownCalls !== undefined && receipt.recordedCostMicros === 0))
        ctx.addIssue({
            code: "custom",
            message: "Only a verified unused reservation is released, and only counted unknown calls settle it.",
        });
});
//# sourceMappingURL=bedtime-native-failure-recovery.js.map